"use client";

// app/citation-integrity/VerifierDemo.tsx
//
// Inline demo for the /citation-integrity explainer (Phase 12i.5b).
// Presets seed the form with one of three pedagogical examples:
//   - Topaz et al. canonical citation (verdict: matched)
//   - Lancet Example C (real DOI + fabricated title → verdict: mismatch)
//   - User-edited (whatever they typed)
//
// The form posts to /api/verify same-origin. No authentication required -
// the verifier endpoint is anonymous-tier accessible. Output renders verdict
// + confidence + the mismatch fields so a reader can see *what differed*.

import { useCallback, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { externalReferrerHeader } from "@/lib/client/external-referrer";
import { cn } from "@/lib/cn";
import { stripHtmlMarkup } from "@/lib/verify/normalize";
import type { Verdict } from "@/lib/verify/verdicts";

type Preset = {
  id: string;
  label: string;
  description: string;
  title: string;
  doi: string;
  authors?: string;
};

const PRESETS: ReadonlyArray<Preset> = [
  {
    id: "topaz-good",
    label: "Topaz et al. (real citation)",
    description:
      "The source paper for this page. Cited title matches the resolved title at this DOI.",
    title: "Fabricated citations: an audit across 2·5 million biomedical papers",
    authors: "Topaz",
    doi: "10.1016/S0140-6736(26)00603-3",
  },
  {
    id: "lancet-c-fabricated",
    label: "Lancet Example C (fabricated)",
    description:
      "Real DOI in Neuroscience (2023). Cited title is invented; resolved paper is the 'ChatGPT in Research' editorial.",
    title:
      "Microglial Modulation via Cannabinoid Receptor 2 Alleviates Fibromyalgia-Related Central Sensitization and Pain Hypersensitivity",
    authors: "Chen",
    doi: "10.1016/j.neuroscience.2023.02.008",
  },
];

type Confidence = "high" | "medium" | "low";

type Mismatch = {
  field: "title" | "first_author" | "year" | "container";
  claimed: string | number | null;
  resolved: string | number | null;
  similarity: number;
};

type ResolvedRecord = {
  title?: string;
  authors?: Array<{ family?: string; given?: string; literal?: string }>;
  container?: { title?: string };
  issued?: { year?: number };
  identifiers?: Array<{ type: string; value: string }>;
};

type VerifyResponse = {
  ok: boolean;
  verdict?: Verdict;
  confidence?: Confidence;
  matched?: ResolvedRecord | null;
  mismatches?: Mismatch[];
  error?: string;
};

type Status = "idle" | "loading" | "ok" | "error";

function verdictTone(v?: Verdict): {
  label: string;
  className: string;
} {
  switch (v) {
    case "matched":
      return {
        label: "Matched",
        className: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
      };
    case "mismatch":
      return {
        label: "Mismatch",
        className: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300",
      };
    case "ambiguous":
      return {
        label: "Ambiguous",
        className: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
      };
    case "not_found":
      return {
        label: "Not found",
        className: "border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-300",
      };
    default:
      return {
        label: v ?? "(none)",
        className: "border-border bg-card text-muted-foreground",
      };
  }
}

function formatAuthorList(
  authors?: Array<{ family?: string; given?: string; literal?: string }>,
): string {
  if (!authors?.length) return "";
  return authors
    .slice(0, 5)
    .map((a) => {
      if (a.literal) return a.literal;
      return [a.family, a.given].filter(Boolean).join(", ");
    })
    .join("; ");
}

export default function VerifierDemo() {
  const [presetId, setPresetId] = useState<string>(PRESETS[0]!.id);
  const [title, setTitle] = useState<string>(PRESETS[0]!.title);
  const [doi, setDoi] = useState<string>(PRESETS[0]!.doi);
  const [authors, setAuthors] = useState<string>(PRESETS[0]!.authors ?? "");
  const [status, setStatus] = useState<Status>("idle");
  const [result, setResult] = useState<VerifyResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const applyPreset = useCallback((id: string) => {
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    setPresetId(id);
    setTitle(p.title);
    setDoi(p.doi);
    setAuthors(p.authors ?? "");
    setResult(null);
    setStatus("idle");
    setErrorMessage(null);
  }, []);

  const onVerify = useCallback(async () => {
    setStatus("loading");
    setErrorMessage(null);
    try {
      const familyOnly = authors.trim().split(/\s*,\s*/)[0];
      const body = {
        claimed: {
          title: title.trim(),
          doi: doi.trim(),
          ...(familyOnly ? { authors: [{ family: familyOnly }] } : {}),
        },
      };
      const res = await fetch("/api/verify", {
        method: "POST",
        headers: { "content-type": "application/json", ...externalReferrerHeader() },
        body: JSON.stringify(body),
      });
      const json: VerifyResponse = await res.json();
      setResult(json);
      setStatus(res.ok && json.ok ? "ok" : "error");
      if (!res.ok || !json.ok) {
        setErrorMessage(json.error ?? `HTTP ${res.status}`);
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Request failed");
    }
  }, [authors, doi, title]);

  const tone = verdictTone(result?.verdict);
  const disabled = status === "loading" || !title.trim() || !doi.trim();
  // Hoist out of JSX. Turbopack/SWC emits an undefined-binding error when
  // `result.matched.title ?? "(no title)"` is inlined inside a `{cond && (...)}`
  // block - the rewriter manufactures a `_result_matched_title` temp that loses
  // its scope. Binding locally is the safe form.
  const matchedRecord = result?.matched ?? null;
  // Upstream registries preserve italic markup and HTML entities in
  // title/container fields. Strip for display.
  const matchedTitle = matchedRecord?.title ? stripHtmlMarkup(matchedRecord.title) : "(no title)";
  const matchedYear = matchedRecord?.issued?.year;
  const matchedContainer = matchedRecord?.container?.title
    ? stripHtmlMarkup(matchedRecord.container.title)
    : undefined;

  return (
    <Card className="space-y-5 p-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">
          Try the verifier on a fabricated citation
        </h2>
        <p className="leading-7 text-muted-foreground">
          Two presets seed the form. Run them to see the verifier flip between{" "}
          <span className="font-medium text-fg">Matched</span> and{" "}
          <span className="font-medium text-fg">Mismatch</span>. Edit any field to test your own
          citation - same call the API would make.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Example citation preset">
        {PRESETS.map((p) => (
          <button
            type="button"
            key={p.id}
            role="radio"
            aria-checked={presetId === p.id}
            onClick={() => applyPreset(p.id)}
            className={cn(
              "rounded-lg border px-3 py-2 text-left text-sm transition",
              presetId === p.id
                ? "border-fg bg-fg/5 text-fg"
                : "border-border bg-card text-muted-foreground hover:border-fg/40 hover:text-fg",
            )}
          >
            <span className="block font-medium">{p.label}</span>
            <span className="block text-xs text-muted-foreground">{p.description}</span>
          </button>
        ))}
      </div>

      <div className="grid gap-3">
        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">Cited title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setPresetId("custom");
            }}
            className="w-full rounded-md border border-border bg-card px-3 py-2 font-mono text-sm"
            placeholder="Paste the title as it appears in the bibliography"
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">DOI (or other identifier)</span>
            <input
              type="text"
              value={doi}
              onChange={(e) => {
                setDoi(e.target.value);
                setPresetId("custom");
              }}
              className="w-full rounded-md border border-border bg-card px-3 py-2 font-mono text-sm"
              placeholder="10.1016/..."
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">First-author family name (optional)</span>
            <input
              type="text"
              value={authors}
              onChange={(e) => {
                setAuthors(e.target.value);
                setPresetId("custom");
              }}
              className="w-full rounded-md border border-border bg-card px-3 py-2 font-mono text-sm"
              placeholder="Topaz"
            />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onVerify} disabled={disabled} aria-busy={status === "loading"}>
          {status === "loading" ? "Verifying…" : "Verify citation"}
        </Button>
        <span className="text-xs text-muted-foreground">
          Calls <code className="font-mono">POST /api/verify</code> - no authentication, free
          anonymous tier.
        </span>
      </div>

      {status === "error" && errorMessage && (
        <div
          role="alert"
          className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300"
        >
          {errorMessage}
        </div>
      )}

      {result?.ok && result.verdict && (
        <div className="space-y-3" aria-live="polite">
          <div className={cn("rounded-lg border px-4 py-3 text-sm", tone.className)}>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide opacity-70">Verdict</span>
              <span className="text-base font-semibold">{tone.label}</span>
              {result.confidence && (
                <span className="rounded bg-black/5 px-1.5 py-0.5 text-xs dark:bg-white/5">
                  {result.confidence} confidence
                </span>
              )}
            </div>
            <p className="mt-1 text-xs opacity-80">
              {result.verdict === "matched" &&
                "The claimed title matches the paper resolved at this identifier."}
              {result.verdict === "mismatch" &&
                "The claimed title does not correspond to the paper at this identifier - Topaz et al.'s dominant fabrication pattern."}
              {result.verdict === "ambiguous" &&
                "The cited identifier resolves to a different paper, but the claimed title matches a real paper elsewhere. Likely wrong-identifier confusion."}
              {result.verdict === "not_found" && "Nothing resolves at this identifier."}
            </p>
          </div>

          {matchedRecord && (
            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Resolved at this identifier
              </p>
              <p className="mt-1 text-base font-semibold text-fg">{matchedTitle}</p>
              <p className="text-sm text-muted-foreground">
                {formatAuthorList(matchedRecord.authors)}
                {matchedYear ? ` · ${matchedYear}` : ""}
                {matchedContainer ? ` · ${matchedContainer}` : ""}
              </p>
            </div>
          )}

          {result.mismatches && result.mismatches.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Field-level differences
              </p>
              <ul className="mt-2 space-y-2 text-sm">
                {result.mismatches.map((m) => (
                  <li key={m.field}>
                    <span className="font-medium text-fg">{m.field}</span>:{" "}
                    <span className="text-muted-foreground">claimed</span>{" "}
                    <span className="font-mono">
                      &ldquo;{stripHtmlMarkup(String(m.claimed ?? "(none)"))}&rdquo;
                    </span>{" "}
                    <span className="text-muted-foreground">vs resolved</span>{" "}
                    <span className="font-mono">
                      &ldquo;{stripHtmlMarkup(String(m.resolved ?? "(none)"))}&rdquo;
                    </span>{" "}
                    <span className="text-xs text-muted-foreground">
                      (similarity {m.similarity.toFixed(2)})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
